import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Users, Clock, UserPlus, CheckCircle } from "lucide-react";
import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    doc,
    getDoc,
} from "firebase/firestore";
import { db, auth } from "../../../Services/firebase-config";
import { useAuth } from "../../../Contexts/AuthContext";

const ViewAvailableTeams = ({ eventId, onBack, currentUserTeam }) => {
    const { userData } = useAuth();
    const [availableTeams, setAvailableTeams] = useState([]);
    const [filteredTeams, setFilteredTeams] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [requestingTeamId, setRequestingTeamId] = useState(null);
    const [existingRequests, setExistingRequests] = useState(new Set());

    const teamsPerPage = 20;

    // Fetch available teams
    const fetchAvailableTeams = useCallback(async () => {
        if (!eventId) return;

        try {
            setIsLoading(true);

            // Query teams for this event where memberIds array length is less than 5
            const teamsQuery = query(
                collection(db, "hackathonTeams"),
                where("eventId", "==", eventId)
            );

            const teamsSnapshot = await getDocs(teamsQuery);
            const teams = teamsSnapshot.docs
                .map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }))
                .filter((team) => team.memberIds && team.memberIds.length < 5);

            setAvailableTeams(teams);
            setFilteredTeams(teams);
        } catch (error) {
            console.error("Error fetching available teams:", error);
            setError("Failed to load available teams");
        } finally {
            setIsLoading(false);
        }
    }, [eventId]);

    // Search effect with debouncing similar to TeamsManagement
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredTeams(availableTeams);
            setCurrentPage(1);
            return;
        }

        setSearchLoading(true);
        const delayed = setTimeout(() => {
            const q = searchTerm.toLowerCase().trim();
            const results = availableTeams.filter(team =>
                (team.name || '').toLowerCase().includes(q) ||
                (team.description || '').toLowerCase().includes(q)
            );
            setFilteredTeams(results);
            setCurrentPage(1);
            setSearchLoading(false);
        }, 300);

        return () => {
            clearTimeout(delayed);
            setSearchLoading(false);
        };
    }, [searchTerm, availableTeams]);

    // Fetch existing requests from current user
    const fetchExistingRequests = useCallback(async () => {
        if (!auth.currentUser || !eventId) return;

        try {
            const requestsQuery = query(
                collection(db, "teamInvitations"),
                where("inviteeEmail", "==", auth.currentUser.email),
                where("eventId", "==", eventId),
                where("status", "==", "pending")
            );

            const requestsSnapshot = await getDocs(requestsQuery);
            const teamIds = new Set(
                requestsSnapshot.docs.map((doc) => doc.data().teamId)
            );

            setExistingRequests(teamIds);
        } catch (error) {
            console.error("Error fetching existing requests:", error);
        }
    }, [eventId]);

    useEffect(() => {
        fetchAvailableTeams();
        fetchExistingRequests();
    }, [fetchAvailableTeams, fetchExistingRequests]);

    // Handle join request
    const handleJoinRequest = async (team) => {
        if (!auth.currentUser || !userData) {
            setError("Please log in to request to join a team");
            return;
        }

        setRequestingTeamId(team.id);
        setError("");

        try {
            // Check if request already exists
            const existingRequestQuery = query(
                collection(db, "teamInvitations"),
                where("inviteeEmail", "==", auth.currentUser.email),
                where("teamId", "==", team.id),
                where("status", "==", "pending")
            );

            const existingRequestSnapshot = await getDocs(existingRequestQuery);

            if (!existingRequestSnapshot.empty) {
                setError("You have already requested to join this team");
                setRequestingTeamId(null);
                return;
            }

            // Get team leader info
            const teamLeaderQuery = query(
                collection(db, "users"),
                where("__name__", "==", team.createdBy)
            );
            const teamLeaderSnapshot = await getDocs(teamLeaderQuery);

            let teamLeaderData = null;
            if (!teamLeaderSnapshot.empty) {
                teamLeaderData = teamLeaderSnapshot.docs[0].data();
            }

            // Create join request (invitation from user to team)
            const requestData = {
                teamId: team.id,
                teamName: team.name,
                eventId: eventId,
                inviterEmail: auth.currentUser.email, // User requesting to join
                inviterId: auth.currentUser.uid,
                inviterName: userData.displayName || userData.name || auth.currentUser.displayName,
                inviteeEmail: teamLeaderData?.email || "", // Team leader email
                status: "pending",
                createdAt: new Date(),
                type: "join_request", // Mark as join request instead of invitation
            };

            // Create notification for team leader
            const notificationData = {
                userId: team.createdBy,
                message: `${userData.displayName || userData.name || auth.currentUser.displayName} wants to join your team "${team.name}"`,
                read: false,
                timestamp: new Date(),
                type: "join_request",
                relatedData: {
                    teamId: team.id,
                    teamName: team.name,
                    eventId: eventId,
                    requesterId: auth.currentUser.uid,
                    requesterEmail: auth.currentUser.email,
                },
            };

            // Create both the request and notification
            await Promise.all([
                addDoc(collection(db, "teamInvitations"), requestData),
                addDoc(collection(db, "notifications"), notificationData),
            ]);

            // Update existing requests set
            setExistingRequests(prev => new Set([...prev, team.id]));

            setSuccessMessage("Join request sent successfully!");
            setTimeout(() => setSuccessMessage(""), 5000);

        } catch (error) {
            console.error("Error sending join request:", error);
            setError("Failed to send join request: " + error.message);
        } finally {
            setRequestingTeamId(null);
        }
    };

    // Pagination logic (use filteredTeams)
    const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);
    const startIndex = (currentPage - 1) * teamsPerPage;
    const endIndex = startIndex + teamsPerPage;
    const currentTeams = filteredTeams.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800">Available Teams</h2>
                </div>

                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-blue-1 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading available teams...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6 flex-wrap">
                <div className="flex items-center">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                </div>

                <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-bold text-gray-800">Available Teams</h2>
                    <span className="text-sm text-gray-500">({filteredTeams.length} teams available)</span>
                </div>

                <div className="w-full sm:w-auto flex items-center gap-2">
                    <input
                        type="text"
                        placeholder="Search teams by name or description..."
                        className="block w-full sm:w-72 pl-3 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1" />
                    )}
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="fixed top-4 right-4 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md shadow-lg max-w-md">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        <span className="text-sm font-medium">{successMessage}</span>
                        <button
                            onClick={() => setSuccessMessage("")}
                            className="ml-4 text-green-700 hover:text-green-900 transition-colors"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
                    {error}
                </div>
            )}

            {/* Teams Grid */}
            {currentTeams.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {currentTeams.map((team) => (
                            <div
                                key={team.id}
                                className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow flex flex-col h-full min-h-[14rem]"
                            >
                                <div className="mb-2">
                                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Team Name</div>
                                    <h3 className="text-base font-semibold text-gray-800 mb-1 line-clamp-2">
                                        {team.name}
                                    </h3>
                                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">About us</div>
                                    <p className="text-sm text-gray-600 line-clamp-3">
                                        {team.description}
                                    </p>
                                </div>

                                {/* Compact spacer so content doesn't overlap action button */}
                                <div className="flex-1" />

                                {/* Action Button pinned to bottom via mt-auto */}
                                <div className="mt-auto">
                                    {existingRequests.has(team.id) ? (
                                        <button
                                            disabled
                                            className="w-full py-2 bg-yellow-100 text-yellow-700 rounded-lg cursor-not-allowed text-sm flex items-center justify-center gap-2"
                                        >
                                            <Clock className="w-4 h-4" />
                                            Request Pending
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleJoinRequest(team)}
                                            disabled={requestingTeamId === team.id}
                                            className="w-full py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 disabled:opacity-50 transition-colors text-sm flex items-center justify-center gap-2"
                                        >
                                            {requestingTeamId === team.id ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <UserPlus className="w-4 h-4" />
                                                    Request to Join
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-8">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${currentPage === page
                                        ? "bg-custom-blue-1 text-white"
                                        : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg mb-2">No available teams found</p>
                    <p className="text-sm">All teams for this event are currently full.</p>
                </div>
            )}
        </div>
    );
};

export default ViewAvailableTeams;